import React from 'react';
import BaseSelect, { components } from 'react-select';
import BaseCreatable from 'react-select/creatable';

// Shared styling for EVERY BlobHub Select, baked in here so every picker is
// identical app-wide (single source of truth — folds in the former
// Styles.headerSelect):
//   • Truly compact control — ~31px tall, 13px text (denser than
//     react-select's 38px default). NOTE: react-select's control height is
//     driven by its indicators, so `minHeight` alone does NOT shrink it — we
//     also trim the value container and dropdown/clear indicator padding.
//     Sizing stays flexible (no fixed heights) so multi/Creatable selects
//     still grow.
//   • No blue focus outline (react-select v5 adds one by default).
//   • menuPortal z-index raised so a portalled menu clears modals and the
//     grid-widget stacking context (only applies when menuPortalTarget is set).
//   • Selected menu row is a subtle accent tint with inherited text, not
//     react-select's default strong-blue-background + white-text. The default is legible for a
//     plain text label, but it clips custom/muted option content (e.g. bold/muted id lines, badges)
//     designed against the surface. Non-selected and hover (`isFocused`) rows get the lighter tint.
// Callers should NOT re-specify control height / indicator padding. Any
// caller-provided styles layer ON TOP of this base per-part (they extend, not
// replace it), so overriding e.g. `menuPortal` z-index keeps the shared sizing.
//
// ---------------------------------------------------------------------------------------------
// THEMING: TWO LEVERS, AND THE ANSWER IS BOTH.
//
// `styles()` is what this file has always used, and it is the right tool for the four things that
// are not theme slots: the compact control, the two option-row tints, the pinned focus border and
// the portal z-index. It is the wrong tool for the palette — reaching every colour through it means
// writing fifteen more per-part functions to say what one seventeen-entry map says.
//
// `theme()` is that map, and it was simply unused: at the time, every `theme=` in src/ was
// AceEditor's `theme="monokai"`. react-select derives roughly two dozen declarations from `theme.colors` — the
// control fill and border, the menu fill, every option row, placeholder, singleValue, input, the
// multiValue chip with its label and remove button, both indicators, the separator, the loading
// dots, noOptionsMessage, groupHeading and every disabled state. Replacing that map re-themes all
// of them at once.
//
// var() STRINGS SURVIVE THE EMOTION PIPELINE, and that is verified here rather than assumed:
//
//   $ node -e "const {serializeStyles} = require('@emotion/serialize');
//              console.log(serializeStyles([{color:'var(--bh-text)',
//                '&:hover':{borderColor:'var(--bh-border-strong)'}}],{}).styles)"
//   color:var(--bh-text);&:hover{border-color:var(--bh-border-strong);}
//
// (@emotion/serialize 1.3.0, the version react-select 5.8 resolves to.) Emotion's only value
// transform is its unitless-number map, which never touches strings, and react-select does no
// colour maths anywhere — grep the package for chroma / tinycolor / color2k / polished and it
// returns nothing, so no value is ever lightened, darkened or alpha-composited.
//
// That matters most for the PORTALLED menu. Eight call sites pass menuPortalTarget={document.body},
// which mounts the menu OUTSIDE the component tree. A var() still resolves there, because custom
// properties inherit from :root and document.body is a child of it. A palette read into JS would
// have had to be re-read and re-pushed into a subtree React does not own.
// ---------------------------------------------------------------------------------------------

// react-select's seventeen-entry palette, mapped onto --bh-*. The stock values are read out of the
// installed dist (Select-49a62830.esm.js) rather than from the docs — the neutral ramp is spelled
// `hsl(0, 0%, N%)` there, not hex.
//
// The ramp runs light-to-dark in react-select's own naming — neutral0 is the CONTROL FILL, neutral80
// the text — which is a light-mode assumption baked into the names. The mapping therefore reads
// inverted in dark and is correct anyway, because every token underneath it flips.
//
//   KEY          WHAT IT PAINTS                                  STOCK      TOKEN
//   neutral0     control fill, menu fill, selected-option ink     #ffffff    --bh-surface
//   neutral5     disabled control fill                            #f2f2f2    --bh-surface-disabled-2
//   neutral10    multiValue chip fill; disabled separator         #e6e6e6    --bh-fill-7
//   neutral20    border, indicators, separator, disabled ink      #cccccc    --bh-border-strong-7
//   neutral30    border on :hover                                 #b3b3b3    --bh-border-emphasis-4
//   neutral40    placeholder, loading dots, disabled singleValue   #999999   --bh-text-subtle-4
//   neutral50    "no options" message                             #808080    --bh-text-subtle-7
//   neutral60    indicator on focus/hover                         #666666    --bh-text-muted-10
//   neutral80    input ink, singleValue, chip label               #333333    --bh-text-4
//   primary      selected row fill, focused border + ring         #2684FF    --bh-accent-2
//   primary50    option :active (press) fill                      #B2D4FF    --bh-accent-tint-strong-3
//   primary25    focused (hover/keyboard) row fill                #DEEBFF    --bh-accent-tint-strong
//   danger       multiValueRemove ink on hover                    #DE350B    --bh-danger-3
//   dangerLight  multiValueRemove fill on hover                   #FFBDAD    --bh-danger-tint-3
//
// Every token's LIGHT value is the stock value it replaces, so this map moves no light pixel.
//
// THREE SLOTS ARE DELIBERATELY LEFT ALONE: neutral70, neutral90 and primary75. No style function in
// react-select 5.8 reads them — measured, by grepping `colors.<key>` across index-a301f526.esm.js
// and Select-49a62830.esm.js: 0 hits each, against 1-5 for every key above. Mapping them would mean
// three more pinned tokens for declarations that cannot render. If a future version starts reading
// one, it will render a light grey in dark; that is the trade, and it is written down here so the
// upgrade that causes it has somewhere to be caught.
const BASE_THEME = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    neutral0: 'var(--bh-surface)',
    neutral5: 'var(--bh-surface-disabled-2)',
    neutral10: 'var(--bh-fill-7)',
    neutral20: 'var(--bh-border-strong-7)',
    neutral30: 'var(--bh-border-emphasis-4)',
    neutral40: 'var(--bh-text-subtle-4)',
    neutral50: 'var(--bh-text-subtle-7)',
    neutral60: 'var(--bh-text-muted-10)',
    neutral80: 'var(--bh-text-4)',
    primary: 'var(--bh-accent-2)',
    primary50: 'var(--bh-accent-tint-strong-3)',
    primary25: 'var(--bh-accent-tint-strong)',
    danger: 'var(--bh-danger-3)',
    dangerLight: 'var(--bh-danger-tint-3)',
  },
});

// Compose a caller's theme over the base, the way mergeStyles composes styles. No caller passes
// `theme` today, but the two props must behave alike or the next caller to reach for one will be
// surprised by the other.
//
// react-select accepts a function OR a plain object, and its own object form is a SHALLOW spread
// over the default theme — which would drop the whole colour map the moment a caller passed
// `{ colors: { primary: 'x' } }`. The object form here merges `colors` one level deeper instead, so
// a caller overriding one entry keeps the other sixteen. A caller who genuinely wants to replace
// the map passes a function; that form is untouched.
function mergeTheme(theme) {
  if (!theme) return BASE_THEME;
  if ('function' === typeof theme) {
    return (base) => theme(BASE_THEME(base));
  }
  return (base) => {
    const merged = BASE_THEME(base);
    return { ...merged, ...theme, colors: { ...merged.colors, ...(theme.colors || {}) } };
  };
}

const BASE_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 31,
    fontSize: '13px',
    boxShadow: 'none',
    // Was the literal '#ced4da' on both branches. --bh-border-strong holds exactly that value in
    // light, so this is identical there — and it now follows the theme instead of pinning a light
    // grey over the themed neutral20 / neutral30 the control would otherwise use.
    borderColor: state.isFocused ? 'var(--bh-border-strong)' : base.borderColor,
    '&:hover': { borderColor: 'var(--bh-border-strong)' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  input: (base) => ({ ...base, margin: '0px' }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  clearIndicator: (base) => ({ ...base, padding: 4 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  // THE MENU IS ITS OWN PLANE, and both declarations here exist for dark.
  //
  // `backgroundColor` comes from neutral0 above, which is the CONTROL fill — one token cannot be
  // both, and under a real elevation ladder a menu floats above the control it belongs to.
  // --bh-surface-overlay is the plane .dropdown-menu, .popover and .modal-content already use, and
  // it is #ffffff in light, exactly like neutral0.
  //
  // react-select's stock shadow is '0 0 0 1px hsla(0,0%,0%,0.1), 0 4px 11px hsla(0,0%,0%,0.1)' — a
  // black alpha, which is nothing at all on a dark ground and leaves a floating menu with no edge.
  // --bh-shadow-menu holds that exact value in light and carries the 1px ring in dark.
  menu: (base) => ({
    ...base,
    backgroundColor: 'var(--bh-surface-overlay)',
    boxShadow: 'var(--bh-shadow-menu)',
  }),
  // Selected/highlighted row tints, app-wide default. react-select's stock look is a strong blue
  // selected row (white text) with a lighter blue hover; we swap in two subtle tints with normal
  // text so custom/muted option content stays legible. The highlighted (hover/keyboard `isFocused`)
  // row gets the lighter --bh-accent-tint-5 (#e6effc in light, the literal that stood here); the
  // persistently-selected row gets --bh-accent-tint-strong (#deebff, likewise) so the current
  // choice reads as the more prominent of the two. isSelected wins when a row is both.
  option: (base, state) => {
    if (state.isSelected) {
      return { ...base, backgroundColor: 'var(--bh-accent-tint-strong)', color: 'inherit' };
    }
    if (state.isFocused) {
      return { ...base, backgroundColor: 'var(--bh-accent-tint-5)' };
    }
    return base;
  },
};

function mergeStyles(styles) {
  if (!styles) return BASE_STYLES;
  const merged = { ...styles };
  // Compose the caller's per-part fn over the shared base fn so both apply.
  for (const key of Object.keys(BASE_STYLES)) {
    const baseFn = BASE_STYLES[key];
    const callerFn = styles[key];
    merged[key] = callerFn
      ? (base, state) => callerFn(baseFn(base, state), state)
      : baseFn;
  }
  return merged;
}

// Exported (alongside `components` below) purely for direct unit testing of the shared per-part
// style fns / composition logic -- CSS-in-JS output is otherwise hard to assert in jsdom.
export { components, BASE_STYLES, BASE_THEME, mergeStyles, mergeTheme };

export const Select = ({ styles, theme, ...props }) => (
  <BaseSelect styles={mergeStyles(styles)} theme={mergeTheme(theme)} {...props} />
);

export const Creatable = ({ styles, theme, ...props }) => (
  <BaseCreatable styles={mergeStyles(styles)} theme={mergeTheme(theme)} {...props} />
);

export default Select;
